import { FC } from 'react';
import { Box, Button } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import {
  DataGridPro,
  GridPagination,
  GridSortDirection,
} from '@mui/x-data-grid-pro';
import {
  ConfirmationModal,
  ReportHistoryModal,
  TableNoResultPlaceholder,
} from '@components';
import { useStableGridModels } from '@common/hooks';
import {
  PinnedColumnMenu,
  TableColumnHeightMapper,
} from 'components/TablePreferences';
import { useReportDataGridTable } from '../hooks/useReportDataGridTable';
import { ReportingTableEntry } from '../hooks/useReportingTable';
import { ReportingTableFilters } from '../hooks/useReportingTable';
import { ReportTableFilters } from './ReportTableFilters';
import { useNavigate } from 'react-router-dom';

interface ReportDataGridProps {
  rows: ReportingTableEntry[];
  totalCount: number;
  loading: boolean;
  filters: ReportingTableFilters;
  setFilters: (filters: ReportingTableFilters) => void;
  handleOpenDeleteModal: (id: string) => void;
  handleMakeReportFavorite: (id: string, isFavorite: boolean) => void;
  makeReportFavoriteIsPending: boolean;
  reportIdToFavorite: string;
}

export const ReportDataGrid: FC<ReportDataGridProps> = ({
  rows,
  totalCount,
  loading,
  filters,
  setFilters,
  handleOpenDeleteModal,
  handleMakeReportFavorite,
  makeReportFavoriteIsPending,
  reportIdToFavorite,
}) => {
  const navigate = useNavigate();

  const {
    apiRef,
    columns,
    handleChangeOrder,
    onPageModelChange,
    handleResetAll,
    initialSortFilters,
    isResetModalOpen,
    setIsResetModalOpen,
    pinnedColumns,
    handlePinnedColumnsChange,
    handleColumnOrderChange,
    handleColumnWidthChange,
    columnVisibilityModel,
    handleColumnVisibilityModelChange,
    density,
    isReportHistoryModalOpen,
    handleCloseHistoryModal,
    historyReportId,
  } = useReportDataGridTable({
    filters,
    setFilters,
    handleOpenDeleteModal,
    handleMakeReportFavorite,
    makeReportFavoriteIsPending,
    reportIdToFavorite,
  });

  const { paginationModel, sortModel, rowCount } = useStableGridModels({
    page: filters.page ?? 1,
    limit: filters.limit ?? 100,
    sort_by: filters.sort_by,
    sort_order: filters.sort_order?.toLowerCase(),
    totalCount,
    loading,
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <ReportTableFilters />

      <DataGridPro
        apiRef={apiRef}
        sx={{
          maxHeight: 'calc(100vh - 440px)',
          '--DataGrid-overlayHeight': 'calc(100vh - 440px)',
          borderRadius: 0,
          boxShadow: 'none',
          border: theme => `1px solid ${theme.palette.grey[300]}`,
          borderBottom: 'none',
          '& .MuiDataGrid-cell': { alignItems: 'center', display: 'flex' },
          '& .MuiDataGrid-row': { cursor: 'pointer' },
        }}
        slots={{
          columnMenu: props => (
            <PinnedColumnMenu {...props} pinnedColumns={pinnedColumns} />
          ),
          pagination: props => (
            <Box
              sx={{
                px: 2,
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Button
                color='primary'
                sx={{ whiteSpace: 'nowrap' }}
                startIcon={<Refresh color='primary' />}
                onClick={() => setIsResetModalOpen(true)}
              >
                RESET VIEW
              </Button>
              <GridPagination {...props} />
            </Box>
          ),
          noRowsOverlay: () => (
            <Box
              sx={{
                height: '100%',
                display: 'grid',
                placeItems: 'center',
                bgcolor: theme => theme.palette.common.white,
              }}
            >
              <TableNoResultPlaceholder />
            </Box>
          ),
        }}
        pinnedColumns={pinnedColumns}
        onPinnedColumnsChange={handlePinnedColumnsChange}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
        onColumnOrderChange={handleColumnOrderChange}
        onColumnWidthChange={handleColumnWidthChange}
        pageSizeOptions={[10, 25, 50, 100]}
        pagination
        paginationModel={paginationModel}
        rows={rows}
        columns={columns}
        rowCount={rowCount}
        loading={loading}
        disableMultipleColumnsSorting
        disableRowSelectionOnClick
        paginationMode='server'
        sortingMode='server'
        disableColumnFilter
        disableColumnSelector
        sortModel={sortModel}
        onPaginationModelChange={onPageModelChange}
        onSortModelChange={model => {
          if (
            model[0] &&
            (model[0].field !== filters.sort_by ||
              model[0].sort !== filters.sort_order?.toLowerCase())
          ) {
            handleChangeOrder(model[0].field, model[0].sort);
          } else {
            handleChangeOrder(
              initialSortFilters.sort_by,
              initialSortFilters.sort_order as GridSortDirection,
            );
          }
        }}
        getRowHeight={() => TableColumnHeightMapper[density ?? 'comfortable']}
        getRowClassName={params =>
          params.indexRelativeToCurrentPage % 2 !== 0 ? 'row-even' : ''
        }
        onRowClick={params => navigate(`/reporting/items/${params.row.id}`)}
      />

      <ConfirmationModal
        title='Reset table view'
        message='This will restore the default columns, sorting, and filters. Your custom layout and pinned columns will be cleared.'
        closeText='KEEP CURRENT VIEW'
        confirmText='RESET VIEW'
        onConfirm={() => {
          handleResetAll();
          setIsResetModalOpen(false);
        }}
        onClose={() => setIsResetModalOpen(false)}
        isOpen={isResetModalOpen}
        reverseButtonOrder
      />

      <ReportHistoryModal
        isOpen={isReportHistoryModalOpen}
        onClose={handleCloseHistoryModal}
        report_id={historyReportId}
      />
    </Box>
  );
};
